import React from 'react'

import { WalletOutlined } from '@ant-design/icons'
import BuildingIcon from '@ant-design/icons/BankOutlined'
import NumberIcon from '@ant-design/icons/NumberOutlined'
import ProjectIcon from '@ant-design/icons/ProjectOutlined'
import UserIcon from '@ant-design/icons/UserOutlined'
import { Popover, Tooltip, Typography } from 'antd'

import { observer } from 'mobx-react-lite'

import { Box, FlexBoxX, FlexBoxY } from 'common/components/boxes'
import { formatName } from 'common/helpers/formatters'
import { getPONumberTitle } from 'common/helpers/get-po-number-title'

import { SelectUser } from 'vendor/components/SelectUser'
import { useVendorStores } from 'vendor/hooks/use-stores'
import { Order } from 'vendor/server/orders'

type OrderInfoProps = {
  order: Order
  watcherIds?: string[]
  onChangeWatchers?: (watcherIds: string[]) => void
  disabled?: boolean
}

type VendorContentProps = {
  vendorContacts: Order['vendor_contacts']
}

const VendorContent = ({ vendorContacts }: VendorContentProps) => (
  <FlexBoxY justifyContent="flex-start" alignItems="flex-start">
    {vendorContacts.map((vendorContact, index) => {
      const safeGlobalVendorContactName = vendorContact?.vendor_user
        ? formatName(vendorContact.vendor_user?.first_name, vendorContact.vendor_user?.last_name)
        : formatName(vendorContact.first_name, vendorContact.last_name)

      return (
        <FlexBoxX key={index} justifyContent="flex-start" alignItems="flex-start" marginBottom="10px">
          {safeGlobalVendorContactName}
          <Typography.Text type="secondary" style={{ marginLeft: '10px' }}>
            {vendorContact.vendor_user?.email || vendorContact.email}
          </Typography.Text>
        </FlexBoxX>
      )
    })}
  </FlexBoxY>
)

const PurchaserContent = ({ full_name }) => <FlexBoxX justifyContent="flex-start">Ordered by: {full_name}</FlexBoxX>

export const OrderInfo = observer<OrderInfoProps>(({ order, watcherIds, onChangeWatchers, disabled }) => {
  const { userStore } = useVendorStores()

  const { order_number, company_user, project, company_vendor, vendor_contacts, commitment } = order

  const safeGlobalVendorName = company_vendor?.vendor?.name || company_vendor?.vendor_name

  const poNumberTitle = getPONumberTitle(order?.state)

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      style={{ gap: 20 }}
      px={{ _: 16, xs: 0 }}
    >
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        justifyContent={{ _: 'space-between', xs: 'flex' }}
        style={{ gap: 20 }}
      >
        <Tooltip title="Project">
          <Box display="flex" alignItems="center">
            <ProjectIcon />
            <Typography.Paragraph style={{ marginBottom: 0, marginLeft: 4 }}>
              {project?.project_id && `${project.project_id}: `}
              {project?.name}
            </Typography.Paragraph>
          </Box>
        </Tooltip>

        <Popover title={company_user?.company_name} content={<PurchaserContent full_name={company_user?.full_name} />}>
          <Box display="flex" alignItems="center">
            <BuildingIcon />
            <Typography.Paragraph style={{ marginBottom: 0, marginLeft: 4 }}>
              {company_user?.company_name}
            </Typography.Paragraph>
          </Box>
        </Popover>

        <Tooltip title={poNumberTitle}>
          <Box display="flex" alignItems="center">
            <NumberIcon />
            <Typography.Paragraph style={{ marginBottom: 0, marginLeft: 4 }}>{order_number}</Typography.Paragraph>
          </Box>
        </Tooltip>

        {!!commitment && (
          <Tooltip title="Commitment">
            <Box display="flex" alignItems="center">
              <WalletOutlined />
              <Typography.Paragraph style={{ marginBottom: 0, marginLeft: 4 }}>
                {commitment?.name}: {commitment?.number}
              </Typography.Paragraph>
            </Box>
          </Tooltip>
        )}
      </Box>
      {/* If not had current user is a public order detail page, so user not able to change watchers */}
      {userStore.currentUser && !disabled ? (
        <SelectUser
          makeOption={(user) => ({
            value: user.id,
            label: user?.name || user?.email,
          })}
          placeholder="Search watchers"
          mode="multiple"
          allowClear={false}
          value={watcherIds}
          onChange={onChangeWatchers}
          boxProps={{ width: 'auto', minWidth: 200 }}
        />
      ) : (
        <Popover
          title={safeGlobalVendorName}
          content={<VendorContent vendorContacts={vendor_contacts} />}
          placement="leftBottom"
        >
          <Box display="flex" alignItems="center">
            <UserIcon />
            <Typography.Paragraph style={{ marginBottom: 0, marginLeft: 4 }}>
              {safeGlobalVendorName}
            </Typography.Paragraph>
          </Box>
        </Popover>
      )}
    </Box>
  )
})
